import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import { toast } from "sonner";
import { format } from "date-fns";
import type {
  billing_ExpiringBFContractDTO,
  billing_ScheduleChangeRequest,
  billing_UpdateContractRequest,
  documents_UploadSubsidyDocumentRequest,
} from "@/api";
import { isInvoiceCollisionError } from "@/utils/contractErrorMessages";
import { expiringBfKeys } from "@/hooks/useExpiringBeitragsfaktoren";
import { subsidyDocumentKeys } from "@/hooks/useSubsidyDocuments";
import { contractVersionKeys } from "@/hooks/useVersionOperations";

/**
 * Inline renewal of a Beitragsfaktor or SBU confirmation from the dashboard
 * widget (#2004). No new write path:
 *
 *  - A new BF is a billing change, so it goes through the existing
 *    ScheduleChange (a new contract version from the BF's valid-from day). An
 *    SBU date entered with it rides along as a case field.
 *  - An SBU-only renewal changes no billing input. A version split from today
 *    would collide with the month's invoice almost every time, so it is an
 *    in-place update of the live version through the existing
 *    PUT /contracts/{id} (case fields never trip its invoice guard). Later
 *    versions of the group (a split dated in the future, e.g. a BF renewal
 *    from next month) copied the old date; each one whose date is earlier
 *    than the new one gets the same update, or the reminder would come back
 *    the day that version starts.
 *
 * The optional confirmations are uploaded afterwards, to the version that now
 * carries the renewal: the new version of a split, else the live one.
 *
 * A KmbB renewal (#2007) is the yearly review meeting: a new cycle end
 * (kmbb_valid_until) is a case field like the SBU date, so it travels the same
 * in-place path, and the two KmbB papers are the optional uploads.
 */

export interface RenewalUpload {
  category: "bf_confirmation" | "sbu_confirmation" | "kmbb_fachstelle" | "kmbb_standortgespraech";
  fileId: string;
  validFrom?: Date;
  validUntil?: Date;
}

export interface RenewalInput {
  row: billing_ExpiringBFContractDTO;
  /** Fraction in [0, 1]; set together with both BF dates for a BF renewal. */
  beitragsfaktor?: number;
  bfValidFrom?: Date;
  bfValidUntil?: Date;
  sbuValidUntil?: Date;
  /** The new end of the KmbB cycle (#2007). */
  kmbbValidUntil?: Date;
  uploads: RenewalUpload[];
}

export type RenewalPlan =
  | { kind: "schedule"; request: billing_ScheduleChangeRequest }
  | { kind: "update"; request: billing_UpdateContractRequest };

export type RenewalPlanError = "nothing" | "bfIncomplete" | "bfOrder";

const day = (d: Date) => ({ time: format(d, "yyyy-MM-dd") });

/** Turns the dialog's input into the one existing call it makes. */
export function buildRenewalPlan(input: RenewalInput, reason: string): RenewalPlan | RenewalPlanError {
  const bfParts = [input.beitragsfaktor, input.bfValidFrom, input.bfValidUntil];
  const bfGiven = bfParts.filter((p) => p !== undefined).length;
  if (bfGiven > 0 && bfGiven < 3) return "bfIncomplete";
  if (bfGiven === 3) {
    const from = input.bfValidFrom as Date;
    const until = input.bfValidUntil as Date;
    if (from.getTime() > until.getTime()) return "bfOrder";
    const request: billing_ScheduleChangeRequest = {
      effective_date: day(from),
      reason,
      beitragsfaktor: input.beitragsfaktor,
      beitragsfaktor_valid_from: day(from),
      beitragsfaktor_valid_until: day(until),
    };
    if (input.sbuValidUntil) request.sbu_valid_until = day(input.sbuValidUntil);
    if (input.kmbbValidUntil) request.kmbb_valid_until = day(input.kmbbValidUntil);
    return { kind: "schedule", request };
  }
  if (input.sbuValidUntil || input.kmbbValidUntil) {
    const request: billing_UpdateContractRequest = {};
    if (input.sbuValidUntil) request.sbu_valid_until = day(input.sbuValidUntil);
    if (input.kmbbValidUntil) request.kmbb_valid_until = day(input.kmbbValidUntil);
    return { kind: "update", request };
  }
  return "nothing";
}

/** The upload request of one confirmation. */
export function renewalUploadRequest(upload: RenewalUpload): documents_UploadSubsidyDocumentRequest {
  return {
    category: upload.category as documents_UploadSubsidyDocumentRequest.category,
    file_id: upload.fileId,
    valid_from: upload.validFrom ? format(upload.validFrom, "yyyy-MM-dd") : undefined,
    valid_until: upload.validUntil ? format(upload.validUntil, "yyyy-MM-dd") : undefined,
  };
}

/** Raised when the renewal was saved but a confirmation upload failed. */
export class RenewalUploadError extends Error {
  constructor(readonly contractId: string) {
    super("renewal saved, upload failed");
    this.name = "RenewalUploadError";
  }
}

const TERMINAL_VERSION_STATUSES = new Set(["cancelled", "terminated", "archived"]);

/**
 * Carries an in-place renewal (SBU and/or KmbB cycle end) to the later live
 * versions of the group: each one whose date is earlier than the new one gets
 * the new date. A later version without the KmbB flag takes no KmbB date.
 */
async function renewLaterVersions(row: billing_ExpiringBFContractDTO, request: billing_UpdateContractRequest) {
  const groupId = row.contract_group_id;
  const newSbu = request.sbu_valid_until?.time;
  const newKmbb = request.kmbb_valid_until?.time;
  if (!groupId || (!newSbu && !newKmbb)) return;
  const { ContractsService } = await import("@/api");
  const list = await ContractsService.getApiV1ContractsGroupVersions(groupId);
  const versions = list.data ?? [];
  const current = versions.find((v) => v.id === row.contract_id);
  if (!current?.version_number) return;
  for (const v of versions) {
    if (!v.id || (v.version_number ?? 0) <= current.version_number) continue;
    if (TERMINAL_VERSION_STATUSES.has((v.status ?? "").toLowerCase())) continue;
    const later = await ContractsService.getApiV1Contracts1(v.id);
    const update: billing_UpdateContractRequest = {};
    const laterSbu = later.sbu_valid_until?.slice(0, 10);
    if (newSbu && !(laterSbu && laterSbu >= newSbu)) update.sbu_valid_until = request.sbu_valid_until;
    const laterKmbb = later.kmbb_valid_until?.slice(0, 10);
    if (newKmbb && later.kmbb && !(laterKmbb && laterKmbb >= newKmbb)) update.kmbb_valid_until = request.kmbb_valid_until;
    if (Object.keys(update).length === 0) continue;
    await ContractsService.putApiV1Contracts(v.id, update);
  }
}

export function useSubsidyRenewal() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  return useMutation({
    mutationFn: async (params: { plan: RenewalPlan; row: billing_ExpiringBFContractDTO; uploads: RenewalUpload[] }) => {
      const { ContractsService } = await import("@/api");
      const contractId = params.row.contract_id ?? "";
      let target = contractId;
      if (params.plan.kind === "schedule") {
        const split = await ContractsService.postApiV1ContractsScheduleChange(contractId, params.plan.request);
        target = split.new_contract_id || contractId;
      } else {
        await ContractsService.putApiV1Contracts(contractId, params.plan.request);
        await renewLaterVersions(params.row, params.plan.request);
      }
      try {
        for (const upload of params.uploads) {
          await ContractsService.postApiV1ContractsSubsidyDocuments(target, renewalUploadRequest(upload));
        }
      } catch {
        throw new RenewalUploadError(target);
      }
      return { contractId: target };
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: expiringBfKeys.all }),
        queryClient.invalidateQueries({ queryKey: contractVersionKeys.all }),
        queryClient.invalidateQueries({ queryKey: subsidyDocumentKeys.all }),
        queryClient.invalidateQueries({ queryKey: ["contracts"] }),
      ]);
    },
    onSuccess: () => {
      toast.success(i18n._(msg({ id: "subsidyRenewal.saved", message: "Renewal saved" })));
    },
    onError: (error: unknown) => {
      // A back-dated BF renewal that collides with an issued invoice opens the
      // shared BlockingInvoicesDialog in the component.
      if (isInvoiceCollisionError(error)) return;
      if (error instanceof RenewalUploadError) {
        toast.error(
          i18n._(
            msg({
              id: "subsidyRenewal.uploadFailed",
              message: "The renewal is saved, but a confirmation could not be uploaded. Add it on the contract.",
            }),
          ),
        );
        return;
      }
      toast.error(i18n._(msg({ id: "subsidyRenewal.failed", message: "Could not save the renewal. Check the dates and try again." })));
    },
  });
}
